<template src='./list.pug' lang='pug'></template>

<script>
import { Event } from 'vue-tables-2'
import PageTitle from '../../../layout/pageTitle/PageTitle'
import ButtonPrimary from '../../../buttons/buttonPrimary/ButtonPrimary'
import ConfirmModal from '../../../modals/confirmModal/ConfirmModal'
import LoadingPlaceholder from '../../../loading/LoadingPlaceholder'
import { trackAnalyticsEvent } from '../../../../utils/analytics.js'
import customSorter from '../../../../utils/customSorter.js'
import { filterByBank, filterByAgency, filterByAccount, filterByStatus } from '../../../../utils/customFilters.js'
import { generateLoadingRows } from '../../../../utils/grid-load-transition.js'
import PATHS from '../../../../router/routesMap.js'

const templatesWhenIsLoaded = {
  nome: (h, row) => row.nome,
  banco: (h, row) => row.banco.codigo + '-' + row.banco.nomeFantasia,
  agencia: (h, row) => row.numAgencia + (row.digAgencia ? '-' + row.digAgencia : ''),
  conta: (h, row) => row.conta + (row.digConta ? '-' + row.digConta : ''),
  ofdaStatus: (h, row) => row.ofdaStatusText,
  status: (h, row) => row.statusText
}

export default {
  name: 'BankAccountsList',

  components: {
    PageTitle,
    ButtonPrimary,
    LoadingPlaceholder,
    ConfirmModal
  },

  data () {
    return {
      bankAccountIdToRemove: null,
      requestSentRemove: false,
      isLoadingData: true,
      showToggleInactiveAccounts: false,
      showInactiveAccounts: false,
      allBankAccounts: null,
      bankAccounts: generateLoadingRows(10),
      columns: ['nome', 'banco', 'agencia', 'conta', 'ofdaStatus', 'status', 'acoes'],
      options: {
        columnsClasses: { 'acoes': 'col-md-1', 'nome': 'col-md-2', 'banco': 'col-md-2', 'agencia': 'col-md-2', 'numeroConta': 'col-md-2', 'status': 'col-md-1', 'ofdaStatus': 'col-md-2' },
        headings: {
          conta: this.$t('contaBancaria.labels.numeroConta'),
          agencia: this.$t('contaBancaria.labels.agencia'),
          banco: this.$t('contaBancaria.labels.banco'),
          nome: this.$t('contaBancaria.labels.nome'),
          ofdaStatus: this.$t('ofda.titulo'),
          status: this.$t('contaBancaria.labels.status'),
          acoes: this.$t('administracao.perfilAcesso.labels.acoes')
        },
        templates: {
          banco: LoadingPlaceholder,
          agencia: LoadingPlaceholder,
          conta: LoadingPlaceholder,
          acoes: LoadingPlaceholder,
          status: LoadingPlaceholder,
          ofdaStatus: LoadingPlaceholder,
          nome: LoadingPlaceholder
        },
        filterable: ['nome'],
        customFilters: [{
          name: 'banco',
          callback: filterByBank
        }, {
          name: 'agencia',
          callback: filterByAgency
        }, {
          name: 'conta',
          callback: filterByAccount
        }, {
          name: 'status',
          callback: filterByStatus
        }],
        filterByColumn: true,
        perPageValues: [],
        texts: {
          count: '',
          noResults: this.$t('dataTable.labels.semResultados'),
          filterBy: '',
          filterPlaceholder: this.$t('dataTable.labels.filtrar')
        },
        sortable: ['nome', 'banco', 'agencia', 'conta', 'status', 'ofdaStatus'],
        sortIcon: {
          is: ''
        },
        pagination: { chunk: 5 },
        perPage: Number.MAX_SAFE_INTEGER,
        customSorting: {
          banco: (ascending) => {
            return (a, b) => {
              let lastA = a.banco.codigo
              let lastB = b.banco.codigo
              if (ascending) {
                return lastA <= lastB ? 1 : -1
              }
              return lastA >= lastB ? 1 : -1
            }
          },
          agencia: (ascending) => {
            return (a, b) => {
              // Cria instÂncia de valor apresentado no template da table para ordenação
              let lastA = a.agencia
              lastA.agencia = a.numAgencia + (a.digAgencia ? '-' + a.digAgencia : '')

              let lastB = b.agencia
              lastB.agencia = b.numAgencia + (b.digAgencia ? '-' + b.digAgencia : '')

              return customSorter.stringSorter(ascending, lastA, lastB, 'agencia')
            }
          },
          conta: function (ascending) {
            return function (a, b) {
              return customSorter.stringSorter(ascending, a, b, 'conta')
            }
          },
          nome: function (ascending) {
            return function (a, b) {
              return customSorter.stringSorter(ascending, a, b, 'nome')
            }
          },
          status: function (ascending) {
            return function (a, b) {
              return customSorter.stringSorter(ascending, a, b, 'statusText')
            }
          }
        },
        cellClasses: {
          ofdaStatus: [
            {
              class: 'status-aceite-3',
              condition: row => !row.optIn
            },
            {
              class: 'status-aceite-0',
              condition: row => row.optIn && row.optIn.statusAceite === '0'
            },
            {
              class: 'status-aceite-1',
              condition: row => row.optIn && ((!!row.optIn.belvoLinkId && row.optIn.statusAceite === '2') || row.optIn.statusAceite === '1')
            },
            {
              class: 'status-aceite-3',
              condition: row => row.optIn && !row.optIn.belvoLinkId && row.optIn.statusAceite === '2'
            }
          ]
        }
      }
    }
  },

  methods: {
    handleFilter (event, column) {
      Event.$emit(`vue-tables.filter::${column}`, event.currentTarget.value)
    },

    edit (bankAccountId) {
      trackAnalyticsEvent(this, 'edit-click', 'edit-conta-bancaria', 'edit-conta-bancaria')

      this.$store.dispatch('setBankAccountToEdit', bankAccountId)
        .then((response) => {
          this.$router.push(PATHS.NEW_BANK_ACCOUNT)
        })
    },

    newBankAccount () {
      this.$router.push(PATHS.NEW_BANK_ACCOUNT)
    },

    loadBankAccounts () {
      let url = '/bankAccounts/'
      this.isLoadingData = true
      this.$store.dispatch('getBankAccounts', url)
        .then((response) => {
          this.allBankAccounts = response.data.data
          this.allBankAccounts.map(acc => {
            acc.statusText = acc.ativo ? this.$t('contaBancaria.labels.ativa') : this.$t('contaBancaria.labels.inativa')
            if (acc.optIn) {
              if (acc.optIn.statusAceite === '0') {
                acc.ofdaStatusText = this.$t('ofda.statusAceite.revogado')
              } else if (acc.optIn.statusAceite === '2') {
                acc.ofdaStatusText = this.$t('ofda.statusAceite.autorizar')
              } else if (acc.optIn.statusAceite === '1') {
                acc.ofdaStatusText = this.$t('ofda.statusAceite.autorizado')
              }
            } else {
              acc.ofdaStatusText = this.$t('ofda.statusAceite.autorizar')
            }
          })
          this.showAccounts()
          this.isLoadingData = false
          this.options.templates = templatesWhenIsLoaded
        })
        .catch((error) => {
          this.$root.$refs.notification.error(error.message)
        })
    },

    showAccounts () {
      this.bankAccounts = this.allBankAccounts
    },

    handleRemove (bankAccountIdToRemove) {
      this.bankAccountIdToRemove = bankAccountIdToRemove
      $('#confirmRemoveBankAccount').modal('show')
    },

    remove () {
      trackAnalyticsEvent(this, 'remove-click', 'remove-conta-bancaria', 'remove-conta-bancaria')
      if (this.bankAccountIdToRemove) {
        this.requestSentRemove = true
        this.$store.dispatch('removeBankAccount', this.bankAccountIdToRemove)
          .then((response) => {
            this.$root.$refs.notification.success('contaBancaria.sucesso.remover')
            this.bankAccountIdToRemove = null
            $('#confirmRemoveBankAccount').modal('hide')
            this.loadBankAccounts()
          })
          .catch((error) => {
            this.$root.$refs.notification.error(error.message)
          })
          .finally(() => {
            this.requestSentRemove = false
          })
      }
    },

    closeModalConfirm () {
      this.bankAccountIdToRemove = null
      $('#confirmRemoveBankAccount').modal('hide')
    }
  },

  created () {
    this.loadBankAccounts()
  }
}
</script>

<style>
.margin-bottom {
  margin-bottom: 10px;
}
.status-aceite-1{
  color: green !important;
  font-weight: bold !important;
}
.status-aceite-0{
  color: red !important;
  font-weight: bold !important;
}
.status-aceite-2{
  color: orange !important;
  font-weight: bold !important;
}
.status-aceite-3{
  color: #147d96 !important;
  font-weight: bold !important;
}
</style>



// WEBPACK FOOTER //
// List.vue?21aa22cc